/**
 * LinkClinicPicker — modal to connect a lead to an existing clinic by hand.
 * Searchable by clinic name / owner name / owner phone. A phone-matched clinic
 * (prospect.matchedClinicId) is pre-highlighted at the top.
 *
 * On pick → linkProspectToClinic (stage becomes "registered", AI summary copied).
 */

import { useMemo, useState } from "react";
import { MagnifyingGlass, Buildings, ShieldCheck } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import ModalCloseButton from "../../../../components/ui/ModalCloseButton";
import { useToast } from "../../../../contexts/ToastContext";
import {
  useGetNivClinicsOverviewQuery,
  useLinkProspectToClinicMutation,
} from "../../../../store/services/nivApi";
import { formatPhoneIL } from "../../../../utils/phoneUtils";
import "./NivLeadPage.css";

const phoneDigits = (raw) => {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.startsWith("972")) d = `0${d.slice(3)}`;
  return d;
};

function LinkClinicPicker({ prospect, author, onClose, onLinked }) {
  const { showSuccess, showError } = useToast();
  const [term, setTerm] = useState("");
  const [busyId, setBusyId] = useState(null);
  const { data: rows = [], isLoading } = useGetNivClinicsOverviewQuery();
  const [linkProspect] = useLinkProspectToClinicMutation();

  const list = useMemo(() => {
    const q = term.trim().toLowerCase();
    const qDigits = phoneDigits(q);
    const filtered = rows.filter((r) => {
      if (r.inactiveByAdmin) return false;
      if (!q) return true;
      return (
        (r.name || "").toLowerCase().includes(q) ||
        (r.ownerName || "").toLowerCase().includes(q) ||
        (qDigits.length >= 3 && phoneDigits(r.ownerPhone).includes(qDigits))
      );
    });
    // Phone-matched clinic first, then newest
    return filtered.sort((a, b) => {
      if (a.clinicId === prospect?.matchedClinicId) return -1;
      if (b.clinicId === prospect?.matchedClinicId) return 1;
      return 0;
    });
  }, [rows, term, prospect?.matchedClinicId]);

  const handlePick = async (row) => {
    setBusyId(row.clinicId);
    try {
      await linkProspect({
        phone: prospect.id,
        clinicId: row.clinicId,
        therapistUid:
          prospect.matchedClinicId === row.clinicId ? prospect.matchedTherapistUid || null : null,
        author,
      }).unwrap();
      showSuccess(`הליד קושר ל־${row.name || "הקליניקה"}`);
      onLinked?.(row);
      onClose();
    } catch (error) {
      console.error("[CK-9806] Error linking lead to clinic:", {
        errorCode: "CK-9806",
        errorMessage: error?.message,
        clinicId: row.clinicId,
        timestamp: new Date().toISOString(),
      });
      showError(error?.message || "הקישור נכשל");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="nlp-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="nlp-modal" role="dialog" aria-modal="true" aria-label="קישור לקליניקה">
        <div className="nlp-modal-head">
          <span className="nlp-modal-icon">
            <Buildings size={18} weight="fill" />
          </span>
          <div className="nlp-modal-titles">
            <span className="nlp-eyebrow">קישור ליד</span>
            <h3 className="nlp-modal-title">בחר קליניקה קיימת</h3>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="nlp-search">
          <MagnifyingGlass size={15} />
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="חיפוש לפי שם קליניקה, בעלים או טלפון…"
            autoFocus
            aria-label="חיפוש קליניקה"
          />
        </div>

        <div className="nlp-modal-body">
          {isLoading ? (
            <ContentLoader size="sm" compact message="טוען קליניקות…" />
          ) : list.length === 0 ? (
            <div className="nlp-modal-empty">לא נמצאו קליניקות</div>
          ) : (
            list.slice(0, 60).map((row) => {
              const isMatch = row.clinicId === prospect?.matchedClinicId;
              const isBusy = busyId === row.clinicId;
              return (
                <button
                  key={row.clinicId}
                  type="button"
                  className={`nlp-pick-row${isMatch ? " is-match" : ""}`}
                  onClick={() => handlePick(row)}
                  disabled={!!busyId}
                >
                  <div className="nlp-pick-main">
                    <strong>{row.name || "ללא שם"}</strong>
                    <span className="nlp-pick-meta">
                      {row.ownerName || "—"}
                      {row.ownerPhone ? ` · ${formatPhoneIL(row.ownerPhone)}` : ""}
                      {row.planName ? ` · ${row.planName}` : ""}
                    </span>
                  </div>
                  {isMatch && (
                    <span className="nlp-badge nlp-badge-success">
                      <ShieldCheck size={11} weight="fill" /> התאמה לפי טלפון
                    </span>
                  )}
                  {isBusy && <LoaderLoop size="xs" />}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default LinkClinicPicker;
